// src/mobile_pages/safety/SafetyBoard.jsx
import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { listSafetyDocs } from "../../api/safety";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import MobileSearchBar from "../../components/common/board/MobileSearchBar";

const STATUS_LABEL = {
  approved: "승인",
  rejected: "반려",
  in_progress: "진행 중",
  submit: "상신",
  cancel: "취소",
  none: "",
};

const DOC_TYPES = [
  { key: "report", label: "안전관리 계획서" },
  { key: "check", label: "안전 점검" },
  { key: "scar", label: "SCAR" },
  { key: "incident", label: "사고 이력" },
  { key: "riskcheck", label: "위험성 평가서" },
];

const getDocTypeLabel = (key) => {
  const found = DOC_TYPES.find((t) => t.key === key);
  return found ? found.label : key || "-";
};

export default function SafetyBoard() {
  const projectId = localStorage.getItem("projectId");
  const navigate = useNavigate();
  const location = useLocation();

  const { doc_type } = useParams();

  const pathname = location.pathname;
  const isBoardPage = pathname.includes("/board");

  const isSingleTypeMode = doc_type && doc_type !== "all";

  const currentDocLabel =
    doc_type && doc_type !== "all"
      ? DOC_TYPES.find((t) => t.key === doc_type)?.label
      : null;

  const ALL_SAFETY_TYPES = DOC_TYPES.map((t) => t.key);
  const [types, setTypes] = useState(() => {
    if (isSingleTypeMode) return [doc_type];
    return ALL_SAFETY_TYPES;
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [q, setQ] = useState("");

  //Changed: 검색 옵션 + 날짜 필터 상태
  const [searchField, setSearchField] = useState("title"); // 제목/작성자/DNS
  const [dateFilter, setDateFilter] = useState("all"); // 전체/6m/3m/1m/custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const toggleType = (t) => {
    setPage(1);
    setTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };
  const docTypeCsv = types.length ? types.join(",") : "";

  useEffect(() => {
    if (isSingleTypeMode) {
      setTypes([doc_type]);
    } else {
      setTypes(ALL_SAFETY_TYPES); // 'all' 혹은 undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc_type]);

  // 목록 조회
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {
          project: projectId,
          q,
          search_field: searchField,
          page,
          page_size: pageSize,
          start_date: startDate,
          end_date: endDate,
        };
        if (docTypeCsv) params.doc_type = docTypeCsv;

        const data = await listSafetyDocs(params);
        setRows(data.results);
        setTotal(data.total);
      } catch (err) {
        console.error("Error loading safety docs:", err);
        setError("문서를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };
    if (projectId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, types, page, pageSize, searchTrigger]);

  const StatusBadge = ({ status }) => {
    const color =
      status === "approved"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : status === "rejected"
        ? "bg-rose-50 text-rose-700 border-rose-200"
        : status === "in_progress" || status === "submit"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-gray-50 text-gray-600 border-gray-200";
    return (
      <span
        className={`inline-flex items-center border rounded-md px-2 py-1 text-[11px] font-medium ${color}`}
      >
        {STATUS_LABEL[status] || "-"}
      </span>
    );
  };

  // ✅ 라우트 맞춰 수정된 부분
  const goDetail = (row) => {
    if (!row.doc_type) return;
    navigate(`/project/${projectId}/safety/${row.doc_type}/${row.dns}/view`);
  };

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1); //Changed: 검색 버튼을 눌러야 증가
  };

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border min-w-0">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="min-w-0 flex-1 overflow-hidden">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {currentDocLabel
                  ? `${currentDocLabel} 목록`
                  : doc_type === "all"
                  ? "안전관리 문서 통합목록"
                  : "안전관리 문서 목록"}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">총 {total}건</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isBoardPage && (
                <button
                  className="btn-create flex-shrink-0"
                  onClick={() =>
                    navigate(`/project/${projectId}/safety/create`)
                  }
                >
                  등록
                </button>
              )}
              {doc_type === "scar" && (
                <button
                  className="btn-create flex-shrink-0"
                  onClick={() =>
                    navigate(
                      `/project/${projectId}/safety/form/1?form_type=CAR`
                    )
                  }
                >
                  작성
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 문서 유형 필터 (전체 목록일 때만) */}
        {doc_type === "all" && (
          <div className="bg-white rounded-lg p-2 border min-w-0 overflow-hidden">
            <div className="flex items-center gap-1 flex-wrap min-w-0">
              {DOC_TYPES.map((t) => {
                const active = types.includes(t.key);
                return (
                  <button
                    key={t.key}
                    onClick={() => toggleType(t.key)}
                    className={`rounded-full border px-2 py-0.5 text-[10px] transition whitespace-nowrap flex-shrink-0 ${
                      active
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setTypes(ALL_SAFETY_TYPES);
                  setPage(1);
                }}
                className="rounded-full border px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 whitespace-nowrap flex-shrink-0"
              >
                전체
              </button>
              <button
                onClick={() => {
                  setTypes([]);
                  setPage(1);
                }}
                className="rounded-full border px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 whitespace-nowrap flex-shrink-0"
              >
                없음
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* 검색 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden min-w-0">
          <div className="p-3 min-w-0">
            <MobileSearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />
          </div>
        </div>

        {/* 문서 리스트 */}
        <div className="space-y-2 min-w-0">
          {loading ? (
            <>
              <div className="bg-white rounded-lg p-4 border animate-pulse">
                <div className="flex justify-between items-start">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                  <div className="h-8 w-8 bg-gray-100 rounded-full" />
                </div>
                <div className="mt-3 flex justify-between">
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border animate-pulse">
                <div className="flex justify-between items-start">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                  <div className="h-8 w-8 bg-gray-100 rounded-full" />
                </div>
                <div className="mt-3 flex justify-between">
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border animate-pulse">
                <div className="flex justify-between items-start">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                  <div className="h-8 w-8 bg-gray-100 rounded-full" />
                </div>
                <div className="mt-3 flex justify-between">
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                </div>
              </div>
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">문서가 없습니다.</p>
              {isBoardPage && (
                <button
                  onClick={() =>
                    navigate(`/project/${projectId}/safety/create`)
                  }
                  className="btn-create mt-4"
                >
                  새 문서 등록
                </button>
              )}
            </div>
          ) : (
            rows.map((row) => (
              <button
                key={row.dns}
                onClick={() => goDetail(row)}
                className="w-full text-left bg-white rounded-lg p-4 border hover:border-blue-300 active:bg-gray-50 transition-all overflow-hidden min-w-0"
              >
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {row.title || "제목 없음"}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs text-gray-500 truncate">
                        {row.created_by?.name || "작성자 미입력"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 min-w-0">
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-[11px]">
                    {getDocTypeLabel(row.doc_type)}
                  </span>
                  {row.status ? (
                    <StatusBadge status={row.status} />
                  ) : (
                    <span className="inline-flex items-center border border-gray-200 rounded-md px-2 py-1 text-[11px] font-medium bg-gray-50 text-gray-600">
                      -
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border min-w-0">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
